import { TextField, Radio, RadioGroup, FormControlLabel, Typography, Container, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import Botao from '../Botao/Botao';
import Modal from '../Modal/Modal';
import { BotaoContainer, ModalContainerEditCommission } from './styles';
import { mascaraCPF, mascaraCNPJ } from '../../utils/mascaras';
import CommissionSalesService from '../../services/CommissionSalesService';
import Snackbars from '../Snackbars';

interface EditCommissionModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onOpen?: () => void;
  idProposalApp: string;
  codMega: string;
  hasWebropaySales: boolean;
  preFilledValues: {
    isAutonomous: boolean;
    totalValue: number;
    installmentsAmount: number;
    firstDueDate: string;
    document: string;
  }
}

const EditCommisionModal = (props: EditCommissionModalProps): any => {
  const { isModalOpen, onClose, onSuccess, onOpen, idProposalApp, codMega, hasWebropaySales, preFilledValues } = props;

  const [totalValue, setTotalValue] = useState(preFilledValues?.totalValue ?? 0);
  const [installments, setInstallments] = useState(preFilledValues?.installmentsAmount ?? 1);
  const [firstDueDate, setFirstDueDate] = useState(preFilledValues?.firstDueDate ?? '');
  const [isAutonomous, setIsAutonomous] = useState(preFilledValues?.isAutonomous ?? false);
  const [cpf, setCpf] = useState(preFilledValues?.isAutonomous ? preFilledValues?.document ?? '' : '');
  const [cnpj, setCnpj] = useState(preFilledValues?.isAutonomous ? '' : preFilledValues?.document ?? '');
  const [isLoading, setIsLoading] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarProps, setSnackbarProps] = React.useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    message: '',
    type: 'info',
  });

  React.useEffect(() => {
    if (isModalOpen && onOpen) {
      onOpen();
    }
  }, [isModalOpen, onOpen]);

  useEffect(() => {
    if (hasWebropaySales && preFilledValues) {
      setTotalValue(preFilledValues.totalValue ?? 0);
      setInstallments(preFilledValues.installmentsAmount ?? 1);
      setFirstDueDate(preFilledValues.firstDueDate ?? '');
      setIsAutonomous(preFilledValues.isAutonomous ?? false);
      setCpf(preFilledValues.isAutonomous ? preFilledValues.document ?? '' : '');
      setCnpj(preFilledValues.isAutonomous ? '' : preFilledValues.document ?? '');
    }
  }, [hasWebropaySales, preFilledValues]);

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(mascaraCPF(e.target.value));
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCnpj(mascaraCNPJ(e.target.value));
  };

  const handleTotalValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const numberValue = Number(value) / 100;
    setTotalValue(numberValue);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleGenerateCustomWeBroPayCommissionSaleInvoice = async () => {
    if (!idProposalApp) {
      setSnackbarProps({
        message: 'ID da proposta não encontrado',
        type: 'error',
      });
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      if (hasWebropaySales && codMega) {
        await CommissionSalesService.deleteWeBroPayCommissionSalesByCodMega(codMega);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const data = await CommissionSalesService.generateCustomWeBroPayCommissionSaleInvoice({
        idProposalApp,
        receiverDocument: isAutonomous ? cpf.replace(/\D/g, "") : cnpj.replace(/\D/g, ""),
        totalCommissionValue: totalValue,
        commissionInstallmentAmount: Number(installments),
        firstInstallmentDate: firstDueDate
      });

      if (data && onSuccess) {
        onSuccess();

        setSnackbarProps({
          message: 'Comissão atualizada com sucesso!',
          type: 'success',
        });
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarProps({
        message: 'Erro ao atualizar comissão',
        type: 'error',
      });
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        width={720}
        onClose={onClose}
      >
        <ModalContainerEditCommission>
          <Typography variant="h6">Editar condição da comissão</Typography>

          <Container className="field-group">
            <TextField
              label="Valor total de comissão"
              variant="outlined"
              size="small"
              value={formatCurrency(totalValue)}
              onChange={handleTotalValueChange}
            />
            <TextField
              label="Qtd. parcelas comissão"
              variant="outlined"
              size="small"
              value={installments}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setInstallments(Number(value));
              }}
              inputProps={{
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
            />
            <TextField
              label="Vencimento da 1° Parcela"
              variant="outlined"
              size="small"
              type="date"
              value={firstDueDate}
              onChange={(e) => setFirstDueDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              className="date-input"
            />
          </Container>

          <div className="radio-group-container">
            <Typography variant="subtitle2">Venda realizada por:</Typography>
            <RadioGroup
              row
              value={isAutonomous ? 'autonomous' : 'agency'}
              onChange={(e) => setIsAutonomous(e.target.value === 'autonomous')}
            >
              <FormControlLabel
                value="agency"
                control={<Radio />}
                label="Corretor com imobiliária"
              />
              <FormControlLabel
                value="autonomous"
                control={<Radio />}
                label="Corretor autônomo"
              />
            </RadioGroup>
          </div>

          {isAutonomous ? (
            <TextField
              label="CPF do corretor"
              variant="outlined"
              size="small"
              value={cpf}
              onChange={handleCPFChange}
              fullWidth
              style={{ marginTop: '16px' }}
            />
          ) : (
            <TextField
              label="CNPJ da imobiliária"
              variant="outlined"
              size="small"
              value={cnpj}
              onChange={handleCNPJChange}
              fullWidth
              style={{ marginTop: '16px' }}
            />
          )}

          <Alert severity="warning" className="alert-warning">
            {isAutonomous ? 'O corretor com o CPF' : 'A imobiliária com o CNPJ'} indicado acima receberá a comissão.
          </Alert>

          <BotaoContainer>
            <Botao
              tipo="salvar"
              tamanho="medio"
              className="btn"
              onClick={handleGenerateCustomWeBroPayCommissionSaleInvoice}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Salvar'
              )}
            </Botao>
            <Botao
              tamanho="medio"
              className="btn"
              onClick={onClose}
            >
              Cancelar
            </Botao>
          </BotaoContainer>
        </ModalContainerEditCommission>
      </Modal>
      <Snackbars
        type={snackbarProps.type}
        handleClose={handleSnackbarClose}
        open={snackbarOpen}
      >
        {snackbarProps.message}
      </Snackbars>
    </>
  );
};

export default EditCommisionModal;
